import React, { useState, useMemo } from "react";
import { Checkbox, Tooltip, Dropdown, notification } from "antd";

export const EmailList = ({ 
  emails = [], 
  onSelectEmail, 
  selectedFolder, 
  onFlagChange,
  mailboxPath,
  onArchive,
  onDelete,
  onMarkAsRead,
  onMarkAsUnread,
  onRefresh,
  onRestore,
  onPermanentDelete,
  searchQuery = "",
  labels = []
}) => {
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());

  const formatTime = (date) => {
    if (!date) return "";
    const dateObj = date instanceof Date ? date : new Date(date);
    const now = new Date();
    if (dateObj.toDateString() === now.toDateString()) {
      return dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return dateObj.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  // Highlight search query in text (highlights all occurrences)
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const parts = [];
    let lastIndex = 0;
    let index = textLower.indexOf(queryLower, lastIndex);
    
    while (index !== -1) {
      // Add text before match
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index));
      }
      
      // Add highlighted match
      const match = text.substring(index, index + query.length);
      parts.push(
        <mark key={index} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
          {match}
        </mark>
      );
      
      lastIndex = index + query.length;
      index = textLower.indexOf(queryLower, lastIndex);
    }
    
    // Add remaining text after last match
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? <>{parts}</> : text;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmailIds(new Set(emails.map((email) => email.id || email.uid)));
    } else {
      setSelectedEmailIds(new Set());
    }
  };

  const handleSelectEmail = (emailId, checked) => {
    const newSelected = new Set(selectedEmailIds);
    if (checked) {
      newSelected.add(emailId);
    } else {
      newSelected.delete(emailId);
    }
    setSelectedEmailIds(newSelected);
  };

  const isAllSelected = useMemo(() => {
    return emails.length > 0 && selectedEmailIds.size === emails.length;
  }, [emails.length, selectedEmailIds.size]);

  const isIndeterminate = useMemo(() => {
    return selectedEmailIds.size > 0 && selectedEmailIds.size < emails.length;
  }, [selectedEmailIds.size, emails.length]);

  // Check if at least one selected email is unread
  const hasUnreadSelected = useMemo(() => {
    if (selectedEmailIds.size === 0) return false;
    return emails.some((email) => {
      const emailId = email.id || email.uid;
      return selectedEmailIds.has(emailId) && !email.isRead;
    });
  }, [selectedEmailIds, emails]);

  // Check if all selected emails are read
  const allSelectedRead = useMemo(() => {
    if (selectedEmailIds.size === 0) return false;
    const selectedEmails = emails.filter((email) => {
      const emailId = email.id || email.uid;
      return selectedEmailIds.has(emailId);
    });
    return selectedEmails.length > 0 && selectedEmails.every((email) => email.isRead);
  }, [selectedEmailIds, emails]);

  const handleBulkArchive = async () => {
    if (!onArchive || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const count = selectedEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    notification.success({
      message: "Email archived",
      duration: 3,
    });
    
    // Process in background
    let errorCount = 0;
    for (const email of selectedEmails) {
      try {
        await onArchive(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error archiving email:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Archive failed",
        duration: 3,
      });
    }
  };

  const handleBulkRestore = async () => {
    if (!onRestore || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const count = selectedEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    notification.success({
      message: "Email restored",
      duration: 3,
    });
    
    // Process in background
    let errorCount = 0;
    for (const email of selectedEmails) {
      try {
        await onRestore(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error restoring email:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Restore failed",
        duration: 3,
      });
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (!onPermanentDelete || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const count = selectedEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    notification.success({
      message: "Email permanently deleted",
      duration: 3,
    });
    
    // Process in background
    let errorCount = 0;
    for (const email of selectedEmails) {
      try {
        await onPermanentDelete(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error permanently deleting email:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Delete failed",
        duration: 3,
      });
    }
  };

  const handleBulkDelete = async () => {
    if (!onDelete || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const count = selectedEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    notification.success({
      message: "Email deleted",
      duration: 3,
    });
    
    // Process in background
    let errorCount = 0;
    for (const email of selectedEmails) {
      try {
        await onDelete(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error deleting email:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Delete failed",
        duration: 3,
      });
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (!onMarkAsRead || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const unreadEmails = selectedEmails.filter((email) => !email.isRead);
    const count = unreadEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    if (count > 0) {
      notification.success({
        message: "Marked as read",
        duration: 3,
      });
    }
    
    // Process in background
    let errorCount = 0;
    for (const email of unreadEmails) {
      try {
        await onMarkAsRead(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error marking email as read:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Mark as read failed",
        duration: 3,
      });
    }
  };

  const handleBulkMarkAsUnread = async () => {
    if (!onMarkAsUnread || !mailboxPath) return;
    const selectedEmails = emails.filter((email) => selectedEmailIds.has(email.id || email.uid));
    const readEmails = selectedEmails.filter((email) => email.isRead);
    const count = readEmails.length;
    setSelectedEmailIds(new Set());
    
    // Show notification immediately
    if (count > 0) {
      notification.success({
        message: "Marked as unread",
        duration: 3,
      });
    }
    
    // Process in background
    let errorCount = 0;
    for (const email of readEmails) {
      try {
        await onMarkAsUnread(mailboxPath, email.id || email.uid);
      } catch (error) {
        console.error("Error marking email as unread:", error);
        errorCount++;
      }
    }
    if (errorCount > 0) {
      notification.error({
        message: "Mark as unread failed",
        duration: 3,
      });
    }
  };

  const handleRowArchive = async (e, email) => {
    e.stopPropagation();
    if (!onArchive || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Email archived",
      duration: 3,
    });
    
    // Process in background
    try {
      await onArchive(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error archiving email:", error);
      notification.error({
        message: "Archive failed",
        duration: 3,
      });
    }
  };

  const handleRowDelete = async (e, email) => {
    e.stopPropagation();
    if (!onDelete || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Email deleted",
      duration: 3,
    });
    
    // Process in background
    try {
      await onDelete(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error deleting email:", error);
      notification.error({
        message: "Delete failed",
        duration: 3,
      });
    }
  };

  const handleRowRestore = async (e, email) => {
    e.stopPropagation();
    if (!onRestore || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Email restored",
      duration: 3,
    });
    
    // Process in background
    try {
      await onRestore(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error restoring email:", error);
      notification.error({
        message: "Restore failed",
        duration: 3,
      });
    }
  };

  const handleRowPermanentDelete = async (e, email) => {
    e.stopPropagation();
    if (!onPermanentDelete || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Email permanently deleted",
      duration: 3,
    });
    
    // Process in background
    try {
      await onPermanentDelete(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error permanently deleting email:", error);
      notification.error({
        message: "Delete failed",
        duration: 3,
      });
    }
  };

  const handleRowMarkAsRead = async (e, email) => {
    e.stopPropagation();
    if (!onMarkAsRead || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Marked as read",
      duration: 3,
    });
    
    // Process in background
    try {
      await onMarkAsRead(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error marking email as read:", error);
      notification.error({
        message: "Mark as read failed",
        duration: 3,
      });
    }
  };

  const handleRowMarkAsUnread = async (e, email) => {
    e.stopPropagation();
    if (!onMarkAsUnread || !mailboxPath) return;
    
    // Show notification immediately
    notification.success({
      message: "Marked as unread",
      duration: 3,
    });
    
    // Process in background
    try {
      await onMarkAsUnread(mailboxPath, email.id || email.uid);
    } catch (error) {
      console.error("Error marking email as unread:", error);
      notification.error({
        message: "Mark as unread failed",
        duration: 3,
      });
    }
  };

  const hasSelection = selectedEmailIds.size > 0;
  const isArchiveFolder = mailboxPath && mailboxPath.toLowerCase().includes("archive");
  const isTrashFolder = mailboxPath && (mailboxPath.toLowerCase().includes("trash") || mailboxPath.toLowerCase().includes("deleted"));
  const isScheduledFolder = mailboxPath === "Scheduled";

  const checkboxDropdownItems = [
    {
      key: "all",
      label: "All",
      onClick: () => handleSelectAll(true),
    },
    {
      key: "none",
      label: "None",
      onClick: () => handleSelectAll(false),
    },
    {
      key: "read",
      label: "Read",
      onClick: () => {
        const readEmails = emails.filter((email) => email.isRead);
        setSelectedEmailIds(new Set(readEmails.map((email) => email.id || email.uid)));
      },
    },
    {
      key: "unread",
      label: "Unread",
      onClick: () => {
        const unreadEmails = emails.filter((email) => !email.isRead);
        setSelectedEmailIds(new Set(unreadEmails.map((email) => email.id || email.uid)));
      },
    },
  ];

  const moreOptionsItems = [
    ...(!isScheduledFolder ? [
      {
        key: "mark-all-read",
        label: "Mark all as read",
        onClick: async () => {
          const unreadEmails = emails.filter((email) => !email.isRead && onMarkAsRead && mailboxPath);
          const count = unreadEmails.length;
          
          // Show notification immediately
          if (count > 0) {
            notification.success({
              message: "Marked all as read",
              duration: 3,
            });
          }
          
          // Process in background
          let errorCount = 0;
          for (const email of unreadEmails) {
            try {
              await onMarkAsRead(mailboxPath, email.id || email.uid);
            } catch (error) {
              console.error("Error marking email as read:", error);
              errorCount++;
            }
          }
          if (errorCount > 0) {
            notification.error({
              message: "Mark as read failed",
              duration: 3,
            });
          }
        },
      },
      {
        key: "mark-all-unread",
        label: "Mark all as unread",
        onClick: async () => {
          const readEmails = emails.filter((email) => email.isRead && onMarkAsUnread && mailboxPath);
          const count = readEmails.length;
          
          // Show notification immediately
          if (count > 0) {
            notification.success({
              message: "Marked all as unread",
              duration: 3,
            });
          }
          
          // Process in background
          let errorCount = 0;
          for (const email of readEmails) {
            try {
              await onMarkAsUnread(mailboxPath, email.id || email.uid);
            } catch (error) {
              console.error("Error marking email as unread:", error);
              errorCount++;
            }
          }
          if (errorCount > 0) {
            notification.error({
              message: "Mark as unread failed",
              duration: 3,
            });
          }
        },
      },
    ] : []),
  ];

  const renderTopBar = () => (
    <div className="flex items-center px-4 py-2 border-b border-gray-200 bg-white">
      {/* Checkbox with Dropdown */}
      <div className="flex items-center mr-2">
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="mr-1"
          disabled={emails.length === 0}
        />
        <Dropdown
          menu={{ items: checkboxDropdownItems }}
          trigger={["click"]}
          placement="bottomLeft"
          disabled={emails.length === 0}
        >
          <button className="text-gray-400 hover:text-gray-600 p-1" disabled={emails.length === 0}>
            <span className="material-icons-outlined text-sm">arrow_drop_down</span>
          </button>
        </Dropdown>
      </div>

      {
        !hasSelection && (
          <>
            <Tooltip title="Refresh" overlayStyle={{ fontSize: "12px" }}>
              <button
                onClick={() => {
                  if (onRefresh) onRefresh();
                }}
                className="text-gray-400 hover:text-gray-600 p-1 mr-2"
              >
                <span className="material-icons-outlined text-lg">refresh</span>
              </button>
            </Tooltip>
            <Dropdown
              menu={{ items: moreOptionsItems }}
              trigger={["click"]}
              placement="bottomLeft"
              disabled={emails.length === 0}
            >
              <Tooltip title="More options" overlayStyle={{ fontSize: "12px" }}>
                <button className="text-gray-400 hover:text-gray-600 p-1 mr-2" disabled={emails.length === 0}>
                  <span className="material-icons-outlined text-lg">more_vert</span>
                </button>
              </Tooltip>
            </Dropdown>
          </>
        )
      }
      {/* Action Buttons - Show when emails are selected */}
      {hasSelection && (
        <>
          <span className="text-sm text-gray-600 mr-2 ml-2">
            {selectedEmailIds.size} selected
          </span>
          <div className="flex items-center space-x-2">
            {isTrashFolder ? (
              <>
                <Tooltip title="Restore" overlayStyle={{ fontSize: "12px" }}>
                  <button
                    onClick={handleBulkRestore}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <span className="material-icons-outlined text-lg">archive</span>
                  </button>
                </Tooltip>
                <Tooltip title="Permanently Delete" overlayStyle={{ fontSize: "12px" }}>
                  <button
                    onClick={handleBulkPermanentDelete}
                    className="text-gray-400 hover:text-red-600 p-1"
                  >
                    <span className="material-icons-outlined text-lg">delete_forever</span>
                  </button>
                </Tooltip>
              </>
            ) : (
              <>
                {!isScheduledFolder && (
                  <Tooltip title="Archive" overlayStyle={{ fontSize: "12px" }}>
                    <button
                      onClick={handleBulkArchive}
                      className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isArchiveFolder}
                    >
                      <span className="material-icons-outlined text-lg">archive</span>
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Delete" overlayStyle={{ fontSize: "12px" }}>
                  <button
                    onClick={handleBulkDelete}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <span className="material-icons-outlined text-lg">delete</span>
                  </button>
                </Tooltip>
              </>
            )}
            {!isScheduledFolder && hasUnreadSelected && (
              <Tooltip title="Mark as Read" overlayStyle={{ fontSize: "12px" }}>
                <button
                  onClick={handleBulkMarkAsRead}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <span className="material-icons-outlined text-lg">mark_email_read</span>
                </button>
              </Tooltip>
            )}
            {!isScheduledFolder && allSelectedRead && (
              <Tooltip title="Mark as Unread" overlayStyle={{ fontSize: "12px" }}>
                <button
                  onClick={handleBulkMarkAsUnread}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <span className="material-icons-outlined text-lg">mark_email_unread</span>
                </button>
              </Tooltip>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (emails.length === 0) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {renderTopBar()}
        <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
          <span className="material-icons-outlined text-6xl mb-4">inbox</span>
          <p>Your {selectedFolder} is empty.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Bar - Always Visible */}
      {renderTopBar()}

      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {emails.map((email) => {
          const emailId = email.id || email.uid;
          const isSelected = selectedEmailIds.has(emailId);
          
          return (
            <div
              key={emailId}
              onClick={() => {
                if (!isSelected) {
                  onSelectEmail(email);
                }
              }}
              className={`group relative flex items-center px-4 py-3 border-b border-gray-100 cursor-pointer hover:shadow-md hover:z-10 transition-all ${
                isSelected
                  ? "bg-primary/5"
                  : email.isRead
                  ? "bg-white"
                  : "font-semibold"
              }`}
            >
              {/* Controls */}
              <div className="flex items-center space-x-3 mr-4 text-gray-400">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectEmail(emailId, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                {!isScheduledFolder && (
                  <button
                    className={`hover:text-yellow-400 ${email.isStarred ? "text-yellow-400" : ""}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (onFlagChange) {
                        // Show notification immediately
                        if (email.isStarred) {
                          notification.success({
                            message: "Star removed",
                            duration: 3,
                          });
                        } else {
                          notification.success({
                            message: "Email starred",
                            duration: 3,
                          });
                        }
                        
                        // Process in background
                        try {
                          if (email.isStarred) {
                            await onFlagChange(email, [], ["\\Flagged"]);
                          } else {
                            await onFlagChange(email, ["\\Flagged"], []);
                          }
                        } catch (error) {
                          console.error("Error changing email flag:", error);
                          notification.error({
                            message: "Star action failed",
                            duration: 3,
                          });
                        }
                      }
                    }}
                  >
                    <span className="material-icons-outlined text-xl">
                      {email.isStarred ? "star" : "star_border"}
                    </span>
                  </button>
                )}
              </div>

              {/* Sender */}
              <div className={`w-48 truncate hidden md:block mr-4 ${!email.isRead ? "text-gray-900 font-semibold" : "text-gray-600"}`}>
                {searchQuery ? highlightText(email.senderName || email.senderEmail || "Unknown", searchQuery) : (email.senderName || email.senderEmail || "Unknown")}
              </div>

              {/* Subject & Preview */}
              <div className="flex-1 truncate flex items-center">
                {/* Labels */}
                {email.labels && email.labels.length > 0 && (
                  <div className="flex gap-1.5 mr-2.5 flex-shrink-0">
                    {email.labels.map((labelId) => {
                      const label = labels.find(l => l.id === labelId);
                      if (!label) return null;
                      return (
                        <span
                          key={labelId}
                          className="text-[10px] px-2 py-0.5 rounded-full text-white font-medium shadow-sm"
                          style={{ backgroundColor: label.color }}
                        >
                          {label.name}
                        </span>
                      );
                    })}
                  </div>
                )}
                <span className={`${!email.isRead ? "text-gray-900 font-semibold" : "text-gray-700"}`}>
                  {searchQuery ? highlightText(email.subject || "(No Subject)", searchQuery) : (email.subject || "(No Subject)")}
                </span>
                {email.preview && (
                  <>
                    <span className="mx-2 text-gray-400">-</span>
                    <span className={`text-gray-500 font-normal truncate ${!email.isRead ? "font-medium" : ""}`}>
                      {searchQuery ? highlightText(email.preview, searchQuery) : email.preview}
                    </span>
                  </>
                )}
              </div>

              {/* Actions on Hover - Only show when no selection */}
              {!hasSelection && (
                <div className="hidden group-hover:flex items-center space-x-2 ml-4 pl-2">
                  {isTrashFolder ? (
                    <>
                      <Tooltip title="Restore" overlayStyle={{ fontSize: "12px" }}>
                        <button
                          className="flex text-gray-400 hover:text-gray-600 p-1"
                          onClick={(e) => handleRowRestore(e, email)}
                        >
                          <span className="material-icons-outlined text-lg leading-none">archive</span>
                        </button>
                      </Tooltip>
                      <Tooltip title="Permanently Delete" overlayStyle={{ fontSize: "12px" }}>
                        <button
                          className="flex text-gray-400 hover:text-red-600 p-1"
                          onClick={(e) => handleRowPermanentDelete(e, email)}
                        >
                          <span className="material-icons-outlined text-lg leading-none">delete_forever</span>
                        </button>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {!isScheduledFolder && (
                        <Tooltip title="Archive" overlayStyle={{ fontSize: "12px" }}>
                          <button
                            className="flex text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => handleRowArchive(e, email)}
                            disabled={isArchiveFolder}
                          >
                            <span className="material-icons-outlined text-lg leading-none">archive</span>
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete" overlayStyle={{ fontSize: "12px" }}>
                        <button
                          className="flex text-gray-400 hover:text-gray-600 p-1"
                          onClick={(e) => handleRowDelete(e, email)}
                        >
                          <span className="material-icons-outlined text-lg leading-none">delete</span>
                        </button>
                      </Tooltip>
                    </>
                  )}
                  {!isScheduledFolder && (
                    <Tooltip
                      placement="topRight"
                      title={email.isRead ? "Mark as Unread" : "Mark as Read"}
                      overlayStyle={{ fontSize: "12px" }}
                    >
                      <button
                        className="flex text-gray-400 hover:text-gray-600 p-1"
                        onClick={(e) => {
                          if (email.isRead) {
                            handleRowMarkAsUnread(e, email);
                          } else {
                            handleRowMarkAsRead(e, email);
                          }
                        }}
                      >
                        <span className="material-icons-outlined text-lg leading-none">
                          {email.isRead ? "mark_email_unread" : "mark_email_read"}
                        </span>
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}

              {/* Time */}
              <div className={`w-24 text-right text-xs text-gray-500 ml-4 ${!hasSelection ? "group-hover:hidden" : ""}`}>
                {formatTime(email.timestamp || email.date)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};





